import React, { useState, useEffect, useMemo } from 'react';
import { X, ClipboardCheck } from 'lucide-react';
import api from '../../api.js';
import { vehicleResourceAPI } from '../../api/vehicles.js';
import { getActiveOrg } from '../../api/core.js';
import { useFormat } from '../../i18n/format.js';

// Default inspection checklist items by type.
// `label` reste la référence FRANÇAISE : c'est elle qui est persistée dans
// inspection_item.label. L'affichage passe par t('inspections.checklist.items.<code>')
// et t('inspections.checklist.categories.<category>').
const DEFAULT_CHECKLIST = {
  pre_trip: [
    { code: 'LIGHTS', label: 'Feux et clignotants', category: 'exterior' },
    { code: 'TIRES', label: 'Pneus et pression', category: 'exterior' },
    { code: 'WINDSHIELD', label: 'Pare-brise et essuie-glaces', category: 'exterior' },
    { code: 'MIRRORS', label: 'Rétroviseurs', category: 'exterior' },
    { code: 'FLUIDS', label: 'Niveaux de liquides', category: 'mechanical' },
    { code: 'BRAKES', label: 'Système de freinage', category: 'mechanical' },
    { code: 'STEERING', label: 'Direction', category: 'mechanical' },
    { code: 'HORN', label: 'Klaxon', category: 'safety' },
    { code: 'SEATBELTS', label: 'Ceintures de sécurité', category: 'safety' },
    { code: 'DASHBOARD', label: 'Voyants tableau de bord', category: 'interior' },
    { code: 'DOCUMENTS', label: 'Documents de bord', category: 'administrative' },
  ],
  post_trip: [
    { code: 'DAMAGE', label: 'Dommages corporels', category: 'exterior' },
    { code: 'CLEANLINESS', label: 'Propreté intérieure', category: 'interior' },
    { code: 'FUEL', label: 'Niveau de carburant', category: 'mechanical' },
    { code: 'MILEAGE', label: 'Kilométrage enregistré', category: 'administrative' },
    { code: 'ISSUES', label: 'Problèmes signalés', category: 'general' },
  ],
  dvir: [
    { code: 'AIR_COMPRESSOR', label: 'Compresseur d\'air', category: 'engine' },
    { code: 'AIR_LINES', label: 'Conduites d\'air', category: 'brakes' },
    { code: 'BATTERY', label: 'Batterie', category: 'electrical' },
    { code: 'BRAKE_ACCESSORIES', label: 'Accessoires de frein', category: 'brakes' },
    { code: 'BRAKE_CONNECTIONS', label: 'Connexions de frein', category: 'brakes' },
    { code: 'BRAKES_PARKING', label: 'Frein de stationnement', category: 'brakes' },
    { code: 'BRAKES_SERVICE', label: 'Freins de service', category: 'brakes' },
    { code: 'CLUTCH', label: 'Embrayage', category: 'transmission' },
    { code: 'COUPLING_DEVICES', label: 'Dispositifs d\'attelage', category: 'trailer' },
    { code: 'DEFROSTER', label: 'Dégivreur', category: 'cabin' },
    { code: 'DRIVE_LINE', label: 'Ligne de transmission', category: 'transmission' },
    { code: 'ENGINE', label: 'Moteur', category: 'engine' },
    { code: 'EXHAUST', label: 'Échappement', category: 'engine' },
    { code: 'FIFTH_WHEEL', label: 'Sellette d\'attelage', category: 'trailer' },
    { code: 'FRAME_ASSEMBLY', label: 'Châssis', category: 'structure' },
    { code: 'FRONT_AXLE', label: 'Essieu avant', category: 'suspension' },
    { code: 'FUEL_TANKS', label: 'Réservoirs de carburant', category: 'fuel' },
    { code: 'HEATER', label: 'Chauffage', category: 'cabin' },
    { code: 'HORN', label: 'Klaxon', category: 'safety' },
    { code: 'LIGHTS_HEAD', label: 'Phares', category: 'lighting' },
    { code: 'LIGHTS_STOP', label: 'Feux stop', category: 'lighting' },
    { code: 'LIGHTS_TAIL', label: 'Feux arrière', category: 'lighting' },
    { code: 'LIGHTS_TURN', label: 'Clignotants', category: 'lighting' },
    { code: 'MIRRORS', label: 'Rétroviseurs', category: 'visibility' },
    { code: 'MUFFLER', label: 'Silencieux', category: 'engine' },
    { code: 'OIL_PRESSURE', label: 'Pression d\'huile', category: 'engine' },
    { code: 'RADIATOR', label: 'Radiateur', category: 'cooling' },
    { code: 'REAR_END', label: 'Train arrière', category: 'suspension' },
    { code: 'REFLECTORS', label: 'Réflecteurs', category: 'safety' },
    { code: 'SAFETY_EQUIPMENT', label: 'Équipement de sécurité', category: 'safety' },
    { code: 'SPRINGS', label: 'Ressorts', category: 'suspension' },
    { code: 'STARTER', label: 'Démarreur', category: 'electrical' },
    { code: 'STEERING', label: 'Direction', category: 'steering' },
    { code: 'TACHOGRAPH', label: 'Chronotachygraphe', category: 'administrative' },
    { code: 'TIRES', label: 'Pneus', category: 'wheels' },
    { code: 'TRANSMISSION', label: 'Transmission', category: 'transmission' },
    { code: 'WHEELS_RIMS', label: 'Roues et jantes', category: 'wheels' },
    { code: 'WINDOWS', label: 'Vitres', category: 'visibility' },
    { code: 'WINDSHIELD_WIPERS', label: 'Essuie-glaces', category: 'visibility' },
    { code: 'OTHER', label: 'Autre', category: 'general' },
  ]
};

// Get default checklist for a type
const getDefaultChecklist = (type) => {
  return DEFAULT_CHECKLIST[type] || DEFAULT_CHECKLIST.pre_trip;
};

function InspectionFormModal({ isOpen, onClose, onSuccess, onMessage, vehicles, drivers, defaultVehicleId }) {
  const { t, fmt } = useFormat();
  const today = new Date().toISOString().split('T')[0];
  const now = new Date().toTimeString().slice(0, 5);

  const [formData, setFormData] = useState({
    // Overwritten as soon as a vehicle is selected (org_id must follow the
    // vehicle's own org, not a guess); this is only the pre-selection default.
    org_id: getActiveOrg() || 'ORG-001',
    vehicle_id: '',
    driver_id: '',
    inspection_date: today,
    inspection_time: now,
    type: 'pre_trip',
    odometer: '',
    status: 'submitted',
    form_version: '1.0'
  });

  const [checklistItems, setChecklistItems] = useState(
    getDefaultChecklist('pre_trip').map(item => ({
      ...item,
      status: 'pass',
      severity: 'minor',
      notes: ''
    }))
  );

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Info, 2: Checklist, 3: Review

  // Track current vehicle mileage for validation
  const selectedVehicle = useMemo(() => {
    if (!formData.vehicle_id) return null;
    return vehicles?.find(v => v.id === Number(formData.vehicle_id));
  }, [formData.vehicle_id, vehicles]);
  const currentMileage = selectedVehicle?.mileage || 0;

  // Pre-fill vehicle when defaultVehicleId is provided
  useEffect(() => {
    if (defaultVehicleId && isOpen && !formData.vehicle_id) {
      const vehicle = vehicles?.find(v => v.id === Number(defaultVehicleId));
      if (vehicle) {
        setFormData(prev => ({
          ...prev,
          vehicle_id: String(vehicle.id),
          org_id: vehicle.org_id || prev.org_id,
          odometer: vehicle.mileage ? String(vehicle.mileage) : prev.odometer
        }));
      }
    }
  }, [defaultVehicleId, isOpen, vehicles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill odometer when vehicle selection changes
  useEffect(() => {
    if (selectedVehicle?.mileage && !formData.odometer) {
      setFormData(prev => ({ ...prev, odometer: String(selectedVehicle.mileage) }));
    }
  }, [selectedVehicle]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update checklist when type changes
  const handleTypeChange = (type) => {
    setFormData({ ...formData, type });
    setChecklistItems(
      getDefaultChecklist(type).map(item => ({
        ...item,
        status: 'pass',
        severity: 'minor',
        notes: ''
      }))
    );
  };

  // Update checklist item
  const updateChecklistItem = (index, field, value) => {
    const updated = [...checklistItems];
    updated[index] = { ...updated[index], [field]: value };
    setChecklistItems(updated);
  };

  // Calculate issues
  const issuesFound = checklistItems.filter(item => item.status === 'fail').length;

  // Submit inspection
  const handleSubmit = async () => {
    if (!formData.vehicle_id) {
      onMessage?.({
        title: t('inspections.messages.errorTitle'),
        message: t('inspections.errors.vehicleRequired'),
        type: 'error'
      });
      return;
    }

    // Validate odometer: prevent decrease
    const odometerNum = formData.odometer ? parseInt(formData.odometer) : null;
    if (odometerNum && currentMileage > 0 && odometerNum < currentMileage) {
      onMessage?.({
        title: t('inspections.errors.odometerTitle'),
        message: t('inspections.errors.odometerBelowCurrent', {
          value: fmt.number(odometerNum),
          current: fmt.number(currentMileage)
        }),
        type: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      // Create inspection
      const inspectionData = {
        ...formData,
        vehicle_id: parseInt(formData.vehicle_id),
        driver_id: formData.driver_id ? parseInt(formData.driver_id) : null,
        odometer: odometerNum,
        issues_found: issuesFound,
        status: issuesFound > 0 ? 'failed' : 'passed'
      };

      const response = await api.inspection.create(inspectionData);

      if (response.ok && response.data) {
        const inspectionId = response.data.id;

        // Create inspection items
        for (const item of checklistItems) {
          await api.inspectionItem.create({
            inspection_id: inspectionId,
            code: item.code,
            label: item.label,
            status: item.status,
            severity: item.status === 'fail' ? item.severity : 'minor',
            notes: item.notes || null
          });
        }

        // Sync odometer → vehicle.mileage (only if higher)
        if (odometerNum && odometerNum > currentMileage) {
          try {
            await api.vehicle.update(parseInt(formData.vehicle_id), { mileage: odometerNum });
          } catch (e) {
            console.warn('Failed to sync inspection odometer to vehicle mileage:', e.message);
          }
        }

        onMessage?.({
          title: t('inspections.messages.successTitle'),
          message: t('inspections.messages.created', { count: issuesFound }),
          type: 'success'
        });
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Error creating inspection:', error);
      onMessage?.({
        title: t('inspections.messages.errorTitle'),
        message: t('inspections.errors.createFailed'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-indigo-500 text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">{t('inspections.form.title')}</h2>
              <p className="text-indigo-100 text-sm">{t('inspections.form.step', { step })}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-indigo-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="bg-indigo-100 px-6 py-2">
          <div className="flex gap-2">
            {[1, 2, 3].map(s => (
              <div
                key={s}
                className={`flex-1 h-2 rounded-full ${s <= step ? 'bg-indigo-500' : 'bg-indigo-200'}`}
              />
            ))}
          </div>
          <div className="flex justify-between text-xs text-indigo-600 mt-1">
            <span>{t('inspections.form.steps.info')}</span>
            <span>{t('inspections.form.steps.checklist')}</span>
            <span>{t('inspections.form.steps.review')}</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('inspections.form.fields.vehicle')} *</label>
                  <select
                    value={formData.vehicle_id}
                    onChange={(e) => {
                      const selected = vehicles?.find(v => v.id === Number(e.target.value));
                      setFormData(prev => ({
                        ...prev,
                        vehicle_id: e.target.value,
                        org_id: selected?.org_id || prev.org_id,
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">{t('inspections.form.placeholders.vehicle')}</option>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.make} {v.model} - {v.license_plate}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('inspections.form.fields.driver')}</label>
                  <select
                    value={formData.driver_id}
                    onChange={(e) => setFormData({ ...formData, driver_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">{t('inspections.form.placeholders.driver')}</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.first_name} {d.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('inspections.form.fields.date')} *</label>
                  <input
                    type="date"
                    value={formData.inspection_date}
                    onChange={(e) => setFormData({ ...formData, inspection_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('inspections.form.fields.time')}</label>
                  <input
                    type="time"
                    value={formData.inspection_time}
                    onChange={(e) => setFormData({ ...formData, inspection_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('inspections.form.fields.odometer')}</label>
                  <input
                    type="number"
                    value={formData.odometer}
                    onChange={(e) => setFormData({ ...formData, odometer: e.target.value })}
                    placeholder={currentMileage > 0 ? `${currentMileage}` : t('inspections.form.placeholders.odometer')}
                    min={currentMileage || 0}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                      formData.odometer && currentMileage > 0 && parseInt(formData.odometer) < currentMileage
                        ? 'border-red-400 bg-red-50'
                        : 'border-gray-300'
                    }`}
                  />
                  {currentMileage > 0 && (
                    <p className="text-xs text-gray-400 mt-1">{t('inspections.form.odometerCurrent', { value: fmt.number(currentMileage) })}</p>
                  )}
                  {formData.odometer && currentMileage > 0 && parseInt(formData.odometer) < currentMileage && (
                    <p className="text-xs text-red-500 mt-0.5">{t('inspections.form.odometerCannotDecrease')}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('inspections.form.fields.type')} *</label>
                <div className="grid grid-cols-3 gap-2">
                  {['pre_trip', 'post_trip', 'dvir', 'daily', 'weekly', 'special'].map(value => ({
                    value,
                    label: t(`inspections.form.typeLabels.${value}`),
                    desc: t(`inspections.form.typeDesc.${value}`),
                  })).map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleTypeChange(type.value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.type === type.value
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-200'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{type.label}</div>
                      <div className="text-xs text-gray-500">{type.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Checklist */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('inspections.checklist.title', { count: checklistItems.length })}
                </h3>
                <div className="flex gap-2">
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                    {t('inspections.checklist.passCount', { count: checklistItems.filter(i => i.status === 'pass').length })}
                  </span>
                  <span className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full">
                    {t('inspections.checklist.failCount', { count: checklistItems.filter(i => i.status === 'fail').length })}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full">
                    {t('inspections.checklist.naCount', { count: checklistItems.filter(i => i.status === 'na').length })}
                  </span>
                </div>
              </div>

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {checklistItems.map((item, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      item.status === 'fail' ? 'border-red-200 bg-red-50' :
                      item.status === 'na' ? 'border-gray-200 bg-gray-50' :
                      'border-green-200 bg-green-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">
                          {t(`inspections.checklist.items.${item.code}`, { defaultValue: item.label })}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({t(`inspections.checklist.categories.${item.category}`, { defaultValue: item.category })})
                        </span>
                      </div>
                      <div className="flex gap-2">
                        {['pass', 'fail', 'na'].map(status => (
                          <button
                            key={status}
                            type="button"
                            onClick={() => updateChecklistItem(index, 'status', status)}
                            className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                              item.status === status
                                ? status === 'pass' ? 'bg-green-500 text-white' :
                                  status === 'fail' ? 'bg-red-500 text-white' :
                                  'bg-gray-500 text-white'
                                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {t(`inspections.itemStatus.${status}`)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Show severity and notes for failed items */}
                    {item.status === 'fail' && (
                      <div className="mt-3 pt-3 border-t border-red-200 space-y-2">
                        <div className="flex gap-4 items-center">
                          <span className="text-sm text-gray-600">{t('inspections.checklist.severityLabel')}</span>
                          {['minor', 'major', 'critical'].map(sev => (
                            <button
                              key={sev}
                              type="button"
                              onClick={() => updateChecklistItem(index, 'severity', sev)}
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                item.severity === sev
                                  ? sev === 'minor' ? 'bg-yellow-500 text-white' :
                                    sev === 'major' ? 'bg-orange-500 text-white' :
                                    'bg-red-600 text-white'
                                  : 'bg-white border border-gray-300 text-gray-600'
                              }`}
                            >
                              {t(`inspections.severity.${sev}`)}
                            </button>
                          ))}
                        </div>
                        <input
                          type="text"
                          placeholder={t('inspections.form.placeholders.issueNotes')}
                          value={item.notes}
                          onChange={(e) => updateChecklistItem(index, 'notes', e.target.value)}
                          className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-6">
              <div className={`p-6 rounded-lg ${issuesFound > 0 ? 'bg-red-50 border-2 border-red-200' : 'bg-green-50 border-2 border-green-200'}`}>
                <div className="text-center">
                  <div className={`text-4xl font-bold ${issuesFound > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {issuesFound > 0 ? t('inspections.review.failedTitle') : t('inspections.review.passedTitle')}
                  </div>
                  <p className="text-gray-600 mt-2">
                    {issuesFound > 0
                      ? t('inspections.review.failedHint', { count: issuesFound })
                      : t('inspections.review.passedHint')
                    }
                  </p>
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('inspections.review.infoTitle')}</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-500">{t('inspections.review.vehicle')}:</span> {vehicles.find(v => v.id === parseInt(formData.vehicle_id))?.license_plate || '-'}</p>
                    <p><span className="text-gray-500">{t('inspections.review.date')}:</span> {formData.inspection_date}</p>
                    <p><span className="text-gray-500">{t('inspections.review.type')}:</span> {t(`inspections.types.${formData.type}`, { defaultValue: formData.type })}</p>
                    <p><span className="text-gray-500">{t('inspections.review.odometer')}:</span> {formData.odometer || '-'} km</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('inspections.review.resultsTitle')}</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-500">{t('inspections.review.checkedPoints')}:</span> {checklistItems.length}</p>
                    <p className="text-green-600"><span className="text-gray-500">{t('inspections.review.conform')}:</span> {checklistItems.filter(i => i.status === 'pass').length}</p>
                    <p className="text-red-600"><span className="text-gray-500">{t('inspections.review.nonConform')}:</span> {checklistItems.filter(i => i.status === 'fail').length}</p>
                    <p className="text-gray-500"><span className="text-gray-500">{t('inspections.review.notApplicable')}:</span> {checklistItems.filter(i => i.status === 'na').length}</p>
                  </div>
                </div>
              </div>

              {/* Failed items list */}
              {issuesFound > 0 && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-900 mb-2">{t('inspections.review.anomaliesTitle')}</h4>
                  <ul className="space-y-2">
                    {checklistItems.filter(i => i.status === 'fail').map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          item.severity === 'critical' ? 'bg-red-600 text-white' :
                          item.severity === 'major' ? 'bg-orange-500 text-white' :
                          'bg-yellow-500 text-white'
                        }`}>
                          {t(`inspections.severityUpper.${item.severity}`)}
                        </span>
                        <span className="text-red-800">
                          {t(`inspections.checklist.items.${item.code}`, { defaultValue: item.label })}
                        </span>
                        {item.notes && <span className="text-red-600 italic">- {item.notes}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-between">
          <button
            type="button"
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            disabled={loading}
          >
            {step === 1 ? t('common.actions.cancel') : t('common.actions.previous')}
          </button>

          {step < 3 ? (
            <button
              type="button"
              onClick={() => {
                if (step === 1 && !formData.vehicle_id) {
                  onMessage?.({
                    title: t('inspections.messages.errorTitle'),
                    message: t('inspections.errors.vehicleRequired'),
                    type: 'error'
                  });
                  return;
                }
                setStep(step + 1);
              }}
              className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
            >
              {t('common.actions.next')}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`px-6 py-2 rounded-lg flex items-center gap-2 ${
                issuesFound > 0
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              } disabled:opacity-50`}
            >
              <ClipboardCheck className="w-5 h-5" />
              {loading ? t('common.states.saving') : issuesFound > 0 ? t('inspections.form.submitFailed') : t('inspections.form.submitPassed')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InspectionFormModal;
